import React, { useState, useMemo } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Alert,
} from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import InfoIcon from '@mui/icons-material/Info';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  ComposedChart,
  Area,
} from 'recharts';
import { useOptimization } from '../../context/OptimizationContext';
import { calculateCombinedWinner } from '../../utils/combinedWinnerUtils';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import Chip from '@mui/material/Chip';
import BoxPlotChart from './BoxPlotChart';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`charts-tabpanel-${index}`}
      aria-labelledby={`charts-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ChartsView: React.FC = () => {
  const { results, convergenceData } = useOptimization();
  const [value, setValue] = useState(0);

  // Get fitness label based on mode
  const getFitnessLabel = () => {
    if (!results?.configuration?.fitnessMode || results.configuration.fitnessMode === 'qos-only') {
      return 'Fitness (QoS)';
    }
    const weights = results.configuration.energyAwareWeights;
    if (weights) {
      return `Fitness (Composite: ${(weights.fitness * 100).toFixed(0)}% QoS, ${(weights.energy * 100).toFixed(0)}% Energy, ${(weights.cost * 100).toFixed(0)}% Cost)`;
    }
    return 'Fitness (Energy-Aware)';
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const chartColors = {
    ga: '#1976d2',
    culture: '#9c27b0',
    frog: '#ed6c02',
    sflaga: '#2e7d32',
    sflaca: '#7b1fa2',
    pso: '#d32f2f',
    gapso: '#00897b',
  };

  // Prepare fitness comparison data
  const fitnessComparisonData = useMemo(() => {
    if (!results) return [];
    return [{
      name: 'Algorithms',
      GA: results.ga?.fitness ?? 0,
      Culture: results.culture?.fitness ?? 0,
      Frog: results.frog?.fitness ?? 0,
      SFLAGA: results.sflaga?.fitness ?? 0,
      SFLACA: results.sflaca?.fitness ?? 0,
      PSO: results.pso?.fitness ?? 0,
      GAPSO: results.gapso?.fitness ?? 0,
    }];
  }, [results]);

  // Prepare convergence data (Scenario 1 = first run, Scenario 2 = second run)
  // For now, we'll use the same data for both scenarios
  const convergenceScenario1 = useMemo(() => {
    if (!convergenceData || convergenceData.ga.length === 0) return [];
    
    const maxLength = Math.max(
      convergenceData.ga.length,
      convergenceData.culture.length,
      convergenceData.frog.length,
      convergenceData.sflaga.length,
      convergenceData.sflaca.length,
      convergenceData.pso.length,
      convergenceData.gapso.length
    );

    const data = [];
    for (let i = 0; i < maxLength; i++) {
      data.push({
        iteration: i + 1,
        GA: convergenceData.ga[i]?.fitness || 0,
        Culture: convergenceData.culture[i]?.fitness || 0,
        Frog: convergenceData.frog[i]?.fitness || 0,
        SFLAGA: convergenceData.sflaga[i]?.fitness || 0,
        SFLACA: convergenceData.sflaca[i]?.fitness || 0,
        PSO: convergenceData.pso[i]?.fitness || 0,
        GAPSO: convergenceData.gapso[i]?.fitness || 0,
      });
    }
    return data;
  }, [convergenceData]);

  // Similarity data (relative to SFLAGA)
  const similarityData = useMemo(() => {
    if (!results || !results.sflaga) return [];
    const sflagaFitness = results.sflaga.fitness;
    if (sflagaFitness === 0) return [];
    
    return [{
      name: 'Similarity',
      GA: results.ga?.fitness ? results.ga.fitness / sflagaFitness : 0,
      Culture: results.culture?.fitness ? results.culture.fitness / sflagaFitness : 0,
      Frog: results.frog?.fitness ? results.frog.fitness / sflagaFitness : 0,
      SFLACA: results.sflaca?.fitness ? results.sflaca.fitness / sflagaFitness : 0,
      PSO: results.pso?.fitness ? results.pso.fitness / sflagaFitness : 0,
      GAPSO: results.gapso?.fitness ? results.gapso.fitness / sflagaFitness : 0,
    }];
  }, [results]);

  // Energy consumption data
  const energyData = useMemo(() => {
    if (!results) return [];
    return [{
      name: 'Energy',
      GA: results.ga?.energy ?? 0,
      Culture: results.culture?.energy ?? 0,
      Frog: results.frog?.energy ?? 0,
      SFLAGA: results.sflaga?.energy ?? 0,
      SFLACA: results.sflaca?.energy ?? 0,
      PSO: results.pso?.energy ?? 0,
      GAPSO: results.gapso?.energy ?? 0,
    }];
  }, [results]);

  // Execution cost data
  const costData = useMemo(() => {
    if (!results) return [];
    return [{
      name: 'Cost',
      GA: results.ga?.cost ?? 0,
      Culture: results.culture?.cost ?? 0,
      Frog: results.frog?.cost ?? 0,
      SFLAGA: results.sflaga?.cost ?? 0,
      SFLACA: results.sflaca?.cost ?? 0,
      PSO: results.pso?.cost ?? 0,
      GAPSO: results.gapso?.cost ?? 0,
    }];
  }, [results]);

  // Response Time data
  const responseTimeData = useMemo(() => {
    if (!results) return [];
    return [{
      name: 'Response Time',
      GA: results.ga?.responseTime ?? 0,
      Culture: results.culture?.responseTime ?? 0,
      Frog: results.frog?.responseTime ?? 0,
      SFLAGA: results.sflaga?.responseTime ?? 0,
      SFLACA: results.sflaca?.responseTime ?? 0,
      PSO: results.pso?.responseTime ?? 0,
      GAPSO: results.gapso?.responseTime ?? 0,
    }];
  }, [results]);

  // Execution Time data
  const executionTimeData = useMemo(() => {
    if (!results) return [];
    // CRITICAL: Do NOT convert missing executionTime to 0 - it corrupts statistics
    // Use undefined for missing values (chart library will handle appropriately)
    return [{
      name: 'Execution Time',
      GA: results.ga?.executionTime,
      Culture: results.culture?.executionTime,
      Frog: results.frog?.executionTime,
      SFLAGA: results.sflaga?.executionTime,
      SFLACA: results.sflaca?.executionTime,
      PSO: results.pso?.executionTime,
      GAPSO: results.gapso?.executionTime,
    }];
  }, [results]);

  // Normalized performance data (all metrics normalized to 0-1 scale)
  const normalizedData = useMemo(() => {
    if (!results) return [];
    
    const algorithms = ['ga', 'culture', 'frog', 'sflaga', 'sflaca', 'pso', 'gapso'] as const;
    const algorithmNames = ['GA', 'Culture', 'Frog', 'SFLAGA', 'SFLACA', 'PSO', 'GAPSO'];
    
    // Find min/max for normalization - filter out undefined results
    const fitnessValues = algorithms
      .filter(key => results[key] !== undefined)
      .map(key => results[key]!.fitness);
    const energyValues = algorithms
      .filter(key => results[key] !== undefined)
      .map(key => results[key]!.energy);
    const costValues = algorithms
      .filter(key => results[key] !== undefined)
      .map(key => results[key]!.cost);
    const responseTimeValues = algorithms
      .filter(key => results[key] !== undefined)
      .map(key => results[key]!.responseTime);
    
    if (fitnessValues.length === 0) return [];
    
    const maxFitness = Math.max(...fitnessValues);
    const minFitness = Math.min(...fitnessValues);
    const maxEnergy = Math.max(...energyValues);
    const minEnergy = Math.min(...energyValues);
    const maxCost = Math.max(...costValues);
    const minCost = Math.min(...costValues);
    const maxResponseTime = Math.max(...responseTimeValues);
    const minResponseTime = Math.min(...responseTimeValues);
    
    const normalize = (value: number, min: number, max: number, reverse: boolean = false) => {
      if (max === min) return 0.5;
      const normalized = (value - min) / (max - min);
      return reverse ? 1 - normalized : normalized;
    };
    
    return algorithms
      .filter(key => results[key] !== undefined)
      .map((key, idx) => {
        const originalIdx = algorithms.indexOf(key);
        return {
          name: algorithmNames[originalIdx],
          Fitness: normalize(results[key]!.fitness, minFitness, maxFitness, false), // Higher is better
          'Energy Efficiency': normalize(results[key]!.energy, minEnergy, maxEnergy, true), // Lower energy = higher efficiency
          'Cost Efficiency': normalize(results[key]!.cost, minCost, maxCost, true), // Lower cost = higher efficiency
          'Response Time Efficiency': normalize(results[key]!.responseTime, minResponseTime, maxResponseTime, true), // Lower RT = higher efficiency
        };
      });
  }, [results]);

  // Multi-metric summary data
  const multiMetricData = useMemo(() => {
    if (!results) return [];
    
    const algorithms = ['ga', 'culture', 'frog', 'sflaga', 'sflaca', 'pso', 'gapso'] as const;
    const algorithmNames = ['GA', 'Culture', 'Frog', 'SFLAGA', 'SFLACA', 'PSO', 'GAPSO'];
    
    return algorithms
      .filter(key => results[key] !== undefined)
      .map((key, idx) => {
        const originalIdx = algorithms.indexOf(key);
        return {
          name: algorithmNames[originalIdx],
          Fitness: results[key]!.fitness,
          Energy: results[key]!.energy,
          Cost: results[key]!.cost,
          ResponseTime: results[key]!.responseTime,
        };
      });
  }, [results]);

  // Convergence speed analysis - calculate improvement rate
  const convergenceSpeedData = useMemo(() => {
    if (!convergenceData || convergenceData.ga.length === 0) return [];
    
    const algorithms = ['ga', 'culture', 'frog', 'sflaga', 'sflaca', 'pso', 'gapso'] as const;
    const algorithmNames = ['GA', 'Culture', 'Frog', 'SFLAGA', 'SFLACA', 'PSO', 'GAPSO'];
    
    return algorithms.map((key, idx) => {
      const data = convergenceData[key];
      if (data.length < 2) return { name: algorithmNames[idx], improvementRate: 0, iterationsTo90Percent: 0 };
      
      const initialFitness = data[0].fitness;
      const finalFitness = data[data.length - 1].fitness;
      const improvementRate = finalFitness > initialFitness 
        ? (finalFitness - initialFitness) / data.length 
        : 0;
      
      // Find iteration where 90% of final improvement is reached
      const targetFitness = initialFitness + (finalFitness - initialFitness) * 0.9;
      let iterationsTo90Percent = data.length;
      for (let i = 0; i < data.length; i++) {
        if (data[i].fitness >= targetFitness) {
          iterationsTo90Percent = i + 1;
          break;
        }
      }
      
      return {
        name: algorithmNames[idx],
        improvementRate: improvementRate * 1000, // Scale for visibility
        iterationsTo90Percent,
      };
    });
  }, [convergenceData]);

  // Box plot data for energy distribution (requires statistics from multiple runs)
  const energyBoxPlotData = useMemo(() => {
    if (!results || !results.statistics) return [];
    
    const algorithms = ['ga', 'culture', 'frog', 'sflaga', 'sflaca', 'pso', 'gapso'] as const;
    const algorithmNames = {
      ga: 'GA',
      culture: 'CA',
      frog: 'SFLA',
      sflaga: 'SFGA',
      sflaca: 'SFLACA',
      pso: 'PSO',
      gapso: 'GAPSO',
    };
    
    return algorithms
      .filter(key => results.statistics && results.statistics[key])
      .map(key => {
        const stats = results.statistics![key].energy;
        // Calculate quartiles (approximation using min, median, max, mean)
        // For proper box plot: Q1 ≈ (min + median) / 2, Q3 ≈ (median + max) / 2
        const q1 = (stats.min + stats.median) / 2;
        const q3 = (stats.median + stats.max) / 2;
        const iqr = q3 - q1;
        
        return {
          name: algorithmNames[key],
          min: stats.min,
          q1: q1,
          median: stats.median,
          q3: q3,
          max: stats.max,
          mean: stats.mean,
          stdDev: stats.stdDev,
          // For visualization purposes
          lowerWhisker: Math.max(stats.min, q1 - 1.5 * iqr),
          upperWhisker: Math.min(stats.max, q3 + 1.5 * iqr),
        };
      });
  }, [results]);

  const hasData = results !== null && convergenceData !== null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <ShowChartIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" component="h2">
          Charts & Visualizations
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {/* First Row of Tabs */}
          <Tabs 
            value={value < 5 ? value : false} 
            onChange={(e, newValue) => {
              if (newValue !== false) handleChange(e, newValue);
            }} 
            variant="scrollable" 
            scrollButtons="auto"
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              minHeight: 48,
              '& .MuiTab-root': { 
                minWidth: { xs: 100, sm: 140 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                textTransform: 'none',
              }
            }}
          >
            <Tab label="Fitness Comparison" onClick={() => setValue(0)} />
            <Tab label="Convergence - S1" onClick={() => setValue(1)} />
            <Tab label="Convergence - S2" onClick={() => setValue(2)} />
            <Tab label="Similarity" onClick={() => setValue(3)} />
            <Tab label="Energy" onClick={() => setValue(4)} />
          </Tabs>
          
          {/* Second Row of Tabs */}
          <Tabs 
            value={value >= 5 && value <= 10 ? value - 5 : false} 
            onChange={(e, newValue) => {
              if (newValue !== false) handleChange(e, newValue + 5);
            }} 
            variant="scrollable" 
            scrollButtons="auto"
            sx={{ 
              minHeight: 48,
              '& .MuiTab-root': { 
                minWidth: { xs: 100, sm: 140 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                textTransform: 'none',
              }
            }}
          >
            <Tab label="Cost" onClick={() => setValue(5)} />
            <Tab label="Response Time" onClick={() => setValue(6)} />
            <Tab label="Execution Time" onClick={() => setValue(7)} />
            <Tab label="Normalized" onClick={() => setValue(8)} />
            <Tab label="Multi-Metric" onClick={() => setValue(9)} />
            <Tab label="Convergence Speed" onClick={() => setValue(10)} />
            <Tab label="Combined Winner" onClick={() => setValue(11)} />
            <Tab label="Energy Box Plot" onClick={() => setValue(12)} />
          </Tabs>
        </Box>
      </Box>

      {/* Fitness Comparison Chart */}
      <TabPanel value={value} index={0}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            {getFitnessLabel()} Comparison Across Algorithms
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              {results?.configuration?.fitnessMode === 'energy-aware' ? (
                <>
                  This bar chart displays the final <strong>composite fitness scores</strong> for all optimization algorithms. 
                  Fitness combines QoS quality, energy consumption, and cost using configurable weights. 
                  <strong> Higher fitness values indicate better solutions.</strong> 
                  Use this chart to compare which algorithm achieved the best energy-aware performance.
                </>
              ) : (
                <>
                  This bar chart displays the final fitness scores for all six optimization algorithms. 
                  Fitness is calculated based on QoS attributes (Response Time, Availability, Throughput, etc.) 
                  using a weighted aggregation formula. <strong>Higher fitness values indicate better solutions.</strong> 
                  Use this chart to quickly compare which algorithm achieved the best overall performance.
                </>
              )}
            </Typography>
          </Alert>
          {!hasData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={fitnessComparisonData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis label={{ value: getFitnessLabel(), angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="GA" fill={chartColors.ga} />
                <Bar dataKey="Culture" fill={chartColors.culture} />
                <Bar dataKey="Frog" fill={chartColors.frog} />
                <Bar dataKey="SFLAGA" fill={chartColors.sflaga} />
                <Bar dataKey="SFLACA" fill={chartColors.sflaca} />
                <Bar dataKey="PSO" fill={chartColors.pso} />
                <Bar dataKey="GAPSO" fill={chartColors.gapso} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Convergence Scenario 1 */}
      <TabPanel value={value} index={1}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Convergence Analysis - Scenario 1
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This line chart shows how each algorithm's fitness improves over iterations (generations). 
              The X-axis represents iteration numbers, and the Y-axis shows fitness values. 
              <strong> Steeper upward curves and higher final values indicate better convergence performance.</strong> 
              Algorithms that converge quickly and reach higher fitness values are generally preferred. 
              Use this chart to understand the optimization behavior and convergence speed of each algorithm.
            </Typography>
          </Alert>
          {!hasData || convergenceScenario1.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={convergenceScenario1}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="iteration" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="GA" stroke={chartColors.ga} name="GA" dot={false} />
                <Line type="monotone" dataKey="Culture" stroke={chartColors.culture} name="Culture" dot={false} />
                <Line type="monotone" dataKey="Frog" stroke={chartColors.frog} name="Frog" dot={false} />
                <Line type="monotone" dataKey="SFLAGA" stroke={chartColors.sflaga} name="SFLAGA" dot={false} />
                <Line type="monotone" dataKey="SFLACA" stroke={chartColors.sflaca} name="SFLACA" dot={false} />
                <Line type="monotone" dataKey="PSO" stroke={chartColors.pso} name="PSO" dot={false} />
                <Line type="monotone" dataKey="GAPSO" stroke={chartColors.gapso} name="GAPSO" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Convergence Scenario 2 (same as Scenario 1 for now) */}
      <TabPanel value={value} index={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Convergence Analysis - Scenario 2
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This line chart displays the convergence behavior in a second scenario (currently using the same data as Scenario 1). 
              Comparing convergence patterns across scenarios helps identify algorithm stability and consistency. 
              <strong> Consistent performance across multiple runs indicates reliable algorithms.</strong> 
              This visualization is useful for analyzing how algorithms perform under different conditions or parameter settings.
            </Typography>
          </Alert>
          {!hasData || convergenceScenario1.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={convergenceScenario1}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="iteration" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="GA" stroke={chartColors.ga} name="GA" dot={false} />
                <Line type="monotone" dataKey="Culture" stroke={chartColors.culture} name="Culture" dot={false} />
                <Line type="monotone" dataKey="Frog" stroke={chartColors.frog} name="Frog" dot={false} />
                <Line type="monotone" dataKey="SFLAGA" stroke={chartColors.sflaga} name="SFLAGA" dot={false} />
                <Line type="monotone" dataKey="SFLACA" stroke={chartColors.sflaca} name="SFLACA" dot={false} />
                <Line type="monotone" dataKey="PSO" stroke={chartColors.pso} name="PSO" dot={false} />
                <Line type="monotone" dataKey="GAPSO" stroke={chartColors.gapso} name="GAPSO" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Similarity Chart */}
      <TabPanel value={value} index={3}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Algorithm Similarity (Relative to SFLAGA)
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This bar chart compares algorithm performance relative to SFLAGA (Shuffled Frog Leaping Algorithm - Genetic Algorithm hybrid). 
              Each bar shows the ratio of an algorithm's fitness to SFLAGA's fitness. 
              <strong> A value of 1.0 means equal performance, values above 1.0 indicate better performance than SFLAGA, 
              and values below 1.0 indicate worse performance.</strong> 
              This comparison helps identify which algorithms produce similar or superior results compared to the hybrid approach.
            </Typography>
          </Alert>
          {!hasData || similarityData.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={similarityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="GA" fill={chartColors.ga} />
                <Bar dataKey="Culture" fill={chartColors.culture} />
                <Bar dataKey="Frog" fill={chartColors.frog} />
                <Bar dataKey="SFLACA" fill={chartColors.sflaca} />
                <Bar dataKey="PSO" fill={chartColors.pso} />
                <Bar dataKey="GAPSO" fill={chartColors.gapso} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Energy Consumption Chart */}
      <TabPanel value={value} index={4}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Energy Consumption Comparison (Joules)
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This bar chart displays the total energy consumption for each algorithm's solution, measured in Joules. 
              Energy is calculated using the formula: Energy = (α × ResponseTime + β × Latency) × Power, 
              where α=0.6, β=0.4, and Power=3.0W. 
              <strong> Lower energy consumption indicates more energy-efficient solutions,</strong> 
              which is crucial for cloud computing and resource-constrained environments. 
              This metric helps identify algorithms that optimize for energy efficiency.
            </Typography>
          </Alert>
          {!hasData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={energyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="GA" fill={chartColors.ga} />
                <Bar dataKey="Culture" fill={chartColors.culture} />
                <Bar dataKey="Frog" fill={chartColors.frog} />
                <Bar dataKey="SFLAGA" fill={chartColors.sflaga} />
                <Bar dataKey="SFLACA" fill={chartColors.sflaca} />
                <Bar dataKey="PSO" fill={chartColors.pso} />
                <Bar dataKey="GAPSO" fill={chartColors.gapso} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Execution Cost Chart */}
      <TabPanel value={value} index={5}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Execution Cost Comparison
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This bar chart shows the execution cost for each algorithm's solution. 
              Cost is calculated as the sum of response times across all tasks, multiplied by a cost coefficient (typically 1.0). 
              <strong> Lower execution costs represent more cost-effective solutions,</strong> 
              which is important for cloud service composition where computational resources are billed based on usage. 
              Use this chart to compare the economic efficiency of different optimization approaches.
            </Typography>
          </Alert>
          {!hasData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={costData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="GA" fill={chartColors.ga} />
                <Bar dataKey="Culture" fill={chartColors.culture} />
                <Bar dataKey="Frog" fill={chartColors.frog} />
                <Bar dataKey="SFLAGA" fill={chartColors.sflaga} />
                <Bar dataKey="SFLACA" fill={chartColors.sflaca} />
                <Bar dataKey="PSO" fill={chartColors.pso} />
                <Bar dataKey="GAPSO" fill={chartColors.gapso} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Response Time Chart */}
      <TabPanel value={value} index={6}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Response Time Comparison
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This bar chart displays the total response time for each algorithm's solution, measured in milliseconds. 
              Response time is a critical QoS metric that represents the time taken to execute all tasks in the service composition. 
              <strong> Lower response times indicate faster solutions,</strong> which are essential for real-time applications and 
              user experience. This metric helps identify algorithms that optimize for latency and system responsiveness.
            </Typography>
          </Alert>
          {!hasData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={responseTimeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="GA" fill={chartColors.ga} />
                <Bar dataKey="Culture" fill={chartColors.culture} />
                <Bar dataKey="Frog" fill={chartColors.frog} />
                <Bar dataKey="SFLAGA" fill={chartColors.sflaga} />
                <Bar dataKey="SFLACA" fill={chartColors.sflaca} />
                <Bar dataKey="PSO" fill={chartColors.pso} />
                <Bar dataKey="GAPSO" fill={chartColors.gapso} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Execution Time Chart */}
      <TabPanel value={value} index={7}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Execution Time Comparison
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This bar chart displays the execution time for each algorithm, measured in milliseconds. 
              Execution time represents the actual computational time required by each algorithm to complete the optimization process. 
              <strong> Lower execution times indicate faster algorithms,</strong> which is crucial for real-time applications and 
              when computation resources are limited. This metric helps identify algorithms that provide a good balance between 
              solution quality and computational efficiency.
            </Typography>
          </Alert>
          {!hasData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={executionTimeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis label={{ value: 'Time (ms)', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="GA" fill={chartColors.ga} />
                <Bar dataKey="Culture" fill={chartColors.culture} />
                <Bar dataKey="Frog" fill={chartColors.frog} />
                <Bar dataKey="SFLAGA" fill={chartColors.sflaga} />
                <Bar dataKey="SFLACA" fill={chartColors.sflaca} />
                <Bar dataKey="PSO" fill={chartColors.pso} />
                <Bar dataKey="GAPSO" fill={chartColors.gapso} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Normalized Performance Chart */}
      <TabPanel value={value} index={8}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Normalized Performance Comparison
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This chart displays all performance metrics normalized to a 0-1 scale, enabling fair comparison across different 
              metric types. All metrics are scaled so that higher values (closer to 1.0) indicate better performance. 
              <strong> This normalization allows you to see which algorithm performs best overall across multiple dimensions.</strong> 
              Efficiency metrics (Energy, Cost, Response Time) are inverted so that lower values become higher scores. 
              This visualization is particularly useful for multi-objective optimization analysis.
            </Typography>
          </Alert>
          {!hasData || normalizedData.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={normalizedData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 1]} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Fitness" fill={chartColors.ga} />
                <Bar dataKey="Energy Efficiency" fill={chartColors.culture} />
                <Bar dataKey="Cost Efficiency" fill={chartColors.frog} />
                <Bar dataKey="Response Time Efficiency" fill={chartColors.sflaga} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Multi-Metric Summary Chart */}
      <TabPanel value={value} index={9}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Multi-Metric Performance Summary
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This composed chart displays all key performance metrics (Fitness, Energy, Cost, Response Time) side-by-side 
              for each algorithm. The chart uses different scales (left Y-axis for Fitness, right Y-axis for other metrics) 
              to enable comprehensive comparison. <strong> This visualization helps identify algorithms that excel in multiple 
              objectives or find trade-offs between different performance criteria.</strong> Use this chart for holistic algorithm 
              evaluation and multi-objective optimization analysis.
            </Typography>
          </Alert>
          {!hasData || multiMetricData.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={multiMetricData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis yAxisId="left" label={{ value: 'Fitness', angle: -90, position: 'insideLeft' }} />
                <YAxis yAxisId="right" orientation="right" label={{ value: 'Energy, Cost, Response Time', angle: 90, position: 'insideRight' }} />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="Fitness" fill={chartColors.ga} name="Fitness" />
                <Area yAxisId="right" type="monotone" dataKey="Energy" fill={chartColors.culture} fillOpacity={0.6} name="Energy (Joules)" />
                <Area yAxisId="right" type="monotone" dataKey="Cost" fill={chartColors.frog} fillOpacity={0.6} name="Cost" />
                <Line yAxisId="right" type="monotone" dataKey="ResponseTime" stroke={chartColors.sflaga} strokeWidth={2} name="Response Time (ms)" />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Convergence Speed Chart */}
      <TabPanel value={value} index={10}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Convergence Speed Analysis
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This chart analyzes the convergence speed of each algorithm through two metrics: Improvement Rate (fitness gain per iteration, scaled) 
              and Iterations to 90% Performance (number of iterations needed to reach 90% of final improvement). 
              <strong> Higher improvement rates and fewer iterations indicate faster convergence,</strong> which is crucial for 
              time-constrained optimization problems. This analysis helps identify algorithms that balance convergence speed 
              with solution quality, valuable for real-world applications where computation time is limited.
            </Typography>
          </Alert>
          {!hasData || convergenceSpeedData.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={convergenceSpeedData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis yAxisId="left" label={{ value: 'Improvement Rate (scaled)', angle: -90, position: 'insideLeft' }} />
                <YAxis yAxisId="right" orientation="right" label={{ value: 'Iterations to 90%', angle: 90, position: 'insideRight' }} />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="improvementRate" fill={chartColors.ga} name="Improvement Rate (×1000)" />
                <Line yAxisId="right" type="monotone" dataKey="iterationsTo90Percent" stroke={chartColors.sflaga} strokeWidth={3} name="Iterations to 90% Performance" />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Combined Winner Chart */}
      <TabPanel value={value} index={11}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Combined Winner Analysis (Best Solution + Lowest Energy)
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This chart shows the combined winner: algorithms that are #1 in BOTH fitness (best solution quality) AND energy (lowest energy consumption). 
              The chart compares fitness rank and energy rank for each algorithm. Lower ranks are better. 
              <strong> Perfect winners have rank #1 in both metrics simultaneously.</strong>
            </Typography>
          </Alert>
          {!results ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view combined winner analysis.
              </Typography>
            </Box>
          ) : (() => {
            const combinedWinner = calculateCombinedWinner(results);
            if (!combinedWinner) {
              return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                  <Typography variant="body2" color="text.secondary">
                    No combined winner data available.
                  </Typography>
                </Box>
              );
            }

            // Prepare scatter plot data
            const scatterData = combinedWinner.rankedAlgorithms.map(alg => ({
              name: alg.name,
              fitness: alg.fitness,
              energy: alg.energy,
              fitnessRank: alg.fitnessRank,
              energyRank: alg.energyRank,
              isPerfect: alg.isPerfectWinner,
              isBestAlt: alg.isBestAlternative,
            }));

            return (
              <Box>
                {/* Winner Highlight */}
                {combinedWinner.hasPerfectWinner && combinedWinner.perfectWinners.length > 0 ? (
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'success.light', borderRadius: 2, border: '2px solid', borderColor: 'success.main' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <EmojiEventsIcon sx={{ color: 'success.main', mr: 1 }} />
                      <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 700 }}>
                        Perfect Winner(s)
                      </Typography>
                    </Box>
                    {combinedWinner.perfectWinners.map(winner => (
                      <Typography key={winner.key} variant="body1" sx={{ fontFamily: 'monospace', color: 'success.dark' }}>
                        {winner.name}: Fitness = {winner.fitness.toFixed(6)} (#1), Energy = {winner.energy.toFixed(6)} J (#1)
                      </Typography>
                    ))}
                  </Box>
                ) : combinedWinner.bestAlternative ? (
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'warning.light', borderRadius: 2, border: '2px solid', borderColor: 'warning.main' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <EmojiEventsIcon sx={{ color: 'warning.main', mr: 1 }} />
                      <Typography variant="h6" sx={{ color: 'warning.main', fontWeight: 700 }}>
                        Best Alternative
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace', color: 'warning.dark' }}>
                      {combinedWinner.bestAlternative.name}: Fitness = {combinedWinner.bestAlternative.fitness.toFixed(6)} 
                      (Rank #{combinedWinner.bestAlternative.fitnessRank}), 
                      Energy = {combinedWinner.bestAlternative.energy.toFixed(6)} J 
                      (Rank #{combinedWinner.bestAlternative.energyRank})
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      No algorithm wins both metrics. This is the closest match.
                    </Typography>
                  </Box>
                ) : null}

                {/* Comparison Chart: Fitness and Energy Ranks */}
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart data={scatterData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis yAxisId="left" label={{ value: 'Fitness Rank', angle: -90, position: 'insideLeft' }} />
                    <YAxis yAxisId="right" orientation="right" label={{ value: 'Energy Rank', angle: 90, position: 'insideRight' }} />
                    <Tooltip 
                      formatter={(value: number, name: string, props: any) => {
                        if (name === 'Fitness Rank') {
                          return [`${value} (Fitness: ${props.payload.fitness.toFixed(6)})`, 'Fitness Rank'];
                        }
                        if (name === 'Energy Rank') {
                          return [`${value} (Energy: ${props.payload.energy.toFixed(6)} J)`, 'Energy Rank'];
                        }
                        return value;
                      }}
                      labelFormatter={(label) => `Algorithm: ${label}`}
                    />
                    <Legend />
                    <Bar 
                      yAxisId="left" 
                      dataKey="fitnessRank" 
                      fill="#1976d2" 
                      name="Fitness Rank (Lower is Better)"
                      opacity={0.7}
                    />
                    <Line 
                      yAxisId="right" 
                      type="monotone" 
                      dataKey="energyRank" 
                      stroke="#ed6c02" 
                      strokeWidth={3} 
                      name="Energy Rank (Lower is Better)"
                      dot={{ r: 6, fill: '#ed6c02' }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>

                {/* Ranking Table */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Combined Ranking
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {combinedWinner.rankedAlgorithms.map((alg, idx) => (
                      <Box
                        key={alg.key}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: alg.isPerfectWinner 
                            ? 'rgba(76, 175, 80, 0.1)' 
                            : alg.isBestAlternative 
                            ? 'rgba(255, 193, 7, 0.1)' 
                            : 'transparent',
                          border: alg.isPerfectWinner || alg.isBestAlternative ? '1px solid' : 'none',
                          borderColor: alg.isPerfectWinner ? 'success.main' : alg.isBestAlternative ? 'warning.main' : 'transparent',
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 30 }}>
                          {idx + 1}.
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: alg.isPerfectWinner || alg.isBestAlternative ? 700 : 500, minWidth: 100 }}>
                          {alg.name}
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                          Fitness: {alg.fitness.toFixed(4)} (Rank #{alg.fitnessRank})
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                          Energy: {alg.energy.toFixed(4)} J (Rank #{alg.energyRank})
                        </Typography>
                        {(alg.isPerfectWinner || alg.isBestAlternative) && (
                          <Chip
                            label={alg.isPerfectWinner ? 'Perfect Winner' : 'Best Alternative'}
                            size="small"
                            color={alg.isPerfectWinner ? 'success' : 'warning'}
                            sx={{ ml: 'auto' }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            );
          })(          )}
        </Paper>
      </TabPanel>

      {/* Energy Box Plot */}
      <TabPanel value={value} index={12}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Statistical Distribution (Box Plot)
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This box plot visualizes the statistical distribution across multiple runs for each algorithm. 
              <strong> The box represents the interquartile range (IQR, 25th-75th percentile)</strong>, 
              the horizontal line inside the box shows the <strong>median</strong>, 
              and the whiskers extend to the minimum and maximum values. 
              <strong> For Energy: Lower boxes indicate better (more energy-efficient) algorithms</strong>. 
              <strong> For Fitness: Higher boxes indicate better solutions</strong>. 
              Smaller box heights indicate more consistent performance across runs. 
              This visualization is ideal for comparing both performance and reliability of algorithms.
            </Typography>
          </Alert>
          {!results || !results.statistics ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                Box plot requires multiple runs with statistics. Please run optimization with numberOfRuns &gt; 1.
              </Typography>
            </Box>
          ) : energyBoxPlotData.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No statistical data available for box plot visualization.
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Metric Selector */}
              <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip 
                  label="Energy (Primary Metric)" 
                  color="primary" 
                  variant="filled"
                  sx={{ fontWeight: 600 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', ml: 1 }}>
                  Showing energy consumption distribution - the primary optimization objective
                </Typography>
              </Box>
              
              {/* Custom Box Plot Chart */}
              <BoxPlotChart data={energyBoxPlotData} height={450} />

              {/* Statistical Summary Table */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Statistical Summary
                </Typography>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                  gap: 2 
                }}>
                  {energyBoxPlotData.map((data) => (
                    <Paper 
                      key={data.name} 
                      variant="outlined" 
                      sx={{ p: 1.5, bgcolor: 'background.default' }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {data.name}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                        Median: {data.median.toFixed(2)} J
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                        Mean: {data.mean.toFixed(2)} J
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                        Std Dev: {data.stdDev.toFixed(2)} J
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                        Range: [{data.min.toFixed(2)}, {data.max.toFixed(2)}]
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </Paper>
      </TabPanel>
    </Box>
  );
};

export default ChartsView;
